import bigDecimal from "js-big-decimal";

export interface FormatNumberOptions {
  /**
   * 舍入模式
   * @default bigDecimal.RoundingModes.DOWN
   */
  roundingMode?: number;
  /**
   * 保留小数位数
   * @default 2
   */
  precision?: number;
  /**
   * 是否需要千分位格式化
   * @default false
   */
  needFormat?: boolean;
  /**
   * 格式化区域设置
   * @default 'en-US'
   */
  locale?: string;
  /**
   * 是否需要裁剪末尾的零
   * @default false
   */
  trimZeros?: boolean;
}

/**
 * 格式化数字，支持大数字处理、舍入模式和千分位格式化
 * @param value - 要处理的数值
 * @param options - 格式化选项
 * @returns 格式化后的字符串
 * @example
 * // 基本使用
 * formatBigNumber("123456.789") // "123456.79"
 *
 * // 指定精度和舍入模式
 * formatBigNumber("123456.789", { precision: 1, roundingMode: bigDecimal.RoundingModes.FLOOR }) // "123456.7"
 *
 * // 使用千分位格式化
 * formatBigNumber("123456.789", { needFormat: true }) // "123,456.79"
 */
export const formatBigNumber = (
  value: string | number | undefined | null,
  options: FormatNumberOptions = {},
): string => {
  const {
    roundingMode = bigDecimal.RoundingModes.DOWN,
    precision = 2,
    needFormat = false,
    locale = "en-US",
    trimZeros = false,
  } = options;

  try {
    // 处理 undefined 或空值
    if (value === undefined || value === null || value === "") {
      return "0";
    }

    // 使用 bigDecimal 处理数值
    let number = new bigDecimal(String(value))
      .round(precision, roundingMode)
      .getValue();

    if (trimZeros) {
      // 直接处理字符串，移除末尾的零，避免 Number() 转换导致的精度丢失
      // 只移除小数点后末尾的零，保留小数点前的数字
      number = number.replace(/\.?0+$/, "");
      // 如果结果为空字符串或只有负号，返回 "0"
      if (!number || number === "-") {
        number = "0";
      }
    }

    const [_, decimalPart] = number.split(".");
    // 如果需要格式化，使用 Intl.NumberFormat
    if (needFormat) {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimalPart?.length || 0,
        maximumFractionDigits: decimalPart?.length || 0,
      }).format(parseFloat(number));
    }

    return number;
  } catch (error) {
    // log.error("formatBigNumber error:", error);
    return "0";
  }
};

/**
 * 将对象中的数字字符串转换为 number 类型（支持嵌套对象和数组）
 * @param data - 要处理的数据（对象、数组或基础类型）
 * @returns 处理后的数据，数字字符串已转换为 number 类型
 *
 * @example
 * // 基础对象
 * convertStringNumbersToNumbers({ a: "123", b: "45.67", c: "hello" })
 * // 返回: { a: 123, b: 45.67, c: "hello" }
 *
 * @example
 * // 嵌套对象
 * convertStringNumbersToNumbers({
 *   user: { age: "25", name: "John" },
 *   metrics: { revenue: "1000.5", count: "10" }
 * })
 * // 返回: {
 * //   user: { age: 25, name: "John" },
 * //   metrics: { revenue: 1000.5, count: 10 }
 * // }
 *
 * @example
 * // 包含数组
 * convertStringNumbersToNumbers({
 *   values: ["123", "45.67", "hello"],
 *   nested: [{ price: "99.9", name: "item" }]
 * })
 * // 返回: {
 * //   values: [123, 45.67, "hello"],
 * //   nested: [{ price: 99.9, name: "item" }]
 * // }
 */
export const convertStringNumbersToNumbers = <T>(data: T): T => {
  // 正则规则：
  // ^-?      可选的负号在开头
  // \d+      至少一个数字（整数部分）
  // (\.\d+)? 可选的小数部分（小数点后必须有数字）
  // $        字符串结束
  const numberPattern = /^-?\d+(\.\d+)?$/;

  // 递归处理函数
  const convert = (value: any): any => {
    // 1. 处理字符串类型
    if (typeof value === "string") {
      // 使用正则检查是否符合数字格式
      if (numberPattern.test(value)) {
        return Number(value);
      }
      return value; // 不匹配则保持原样
    }

    // 2. 处理数组类型（递归处理每个元素）
    if (Array.isArray(value)) {
      return value.map((item) => convert(item));
    }

    // 3. 处理对象类型（递归处理每个属性）
    if (value !== null && typeof value === "object") {
      const result: Record<string, any> = {};
      Object.keys(value).forEach((key) => {
        result[key] = convert(value[key]);
      });
      return result;
    }

    // 4. 其他类型（number, boolean, null, undefined 等）直接返回
    return value;
  };

  return convert(data) as T;
};

/**
 * 格式化数字的有效位数
 * @param value - 要处理的数值
 * @param integerPrecision - 整数部分大于0时保留的小数位数，默认为2
 * @param decimalPrecision - 整数部分等于0时保留的小数位数，默认为4
 * @returns 格式化后的数字字符串或null
 *
 * @example
 * // 使用默认配置：整数保留2位小数，纯小数保留4位小数
 * formatSignificantDigits("123.456") // "123.45"
 * formatSignificantDigits("0.001234") // "0.0012"
 * formatSignificantDigits("0") // "0.00"
 *
 * // 自定义配置
 * formatSignificantDigits("12.2345", 2) // "12.23"
 * formatSignificantDigits("0.12345", 2, 3) // "0.123"
 * formatSignificantDigits("0", 3) // "0.000"
 */
export const formatSignificantDigits = (
  value: string | number | undefined | null,
  integerPrecision: number = 2,
  decimalPrecision: number = 4,
): string | null => {
  try {
    // 处理 undefined 或空值
    if (value === undefined || value === null || value === "") {
      return null;
    }

    // 使用 BigDecimal 处理数值
    const valueBD = new bigDecimal(String(value));
    const valueStr = valueBD.getValue();

    // 检查是否为负数
    const isNegative = valueStr.startsWith("-");
    const absoluteValueStr = isNegative ? valueStr.substring(1) : valueStr;
    const [integerPart, decimalPart = ""] = absoluteValueStr.split(".");

    const integerPartBD = new bigDecimal(integerPart || "0");
    const zeroBD = new bigDecimal("0");
    const integerPartCompare = integerPartBD.compareTo(zeroBD);

    let result: string;

    if (integerPartCompare > 0) {
      // 整数部分大于0，按照指定的小数位数格式化
      const roundedValue = new bigDecimal(valueStr).round(
        integerPrecision,
        bigDecimal.RoundingModes.DOWN,
      );
      result = roundedValue.getValue();

      // 检查舍入后是否有非零小数部分
      const [, roundedDecimal = ""] = result.split(".");
      const hasNonZeroDecimal =
        roundedDecimal && roundedDecimal.replace(/0+$/, "").length > 0;

      if (hasNonZeroDecimal) {
        // 有非零小数部分，移除尾随零
        result = result.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
      } else if (roundedDecimal) {
        // 小数部分全为0，移除小数点和小数部分
        result = result.split(".")[0];
      }
    } else if (integerPartCompare === 0) {
      // 整数部分等于0的情况
      // 检查小数部分是否全为0
      const isZero = !decimalPart || decimalPart.replace(/0+$/, "") === "";

      if (!isZero) {
        // 有非零小数部分，按照指定的小数位数格式化
        const roundedValue = new bigDecimal(valueStr).round(
          decimalPrecision,
          bigDecimal.RoundingModes.DOWN,
        );
        result = roundedValue.getValue();

        // 检查舍入后是否有非零小数部分
        const [, roundedDecimal = ""] = result.split(".");
        const hasNonZeroDecimal =
          roundedDecimal && roundedDecimal.replace(/0+$/, "").length > 0;

        if (hasNonZeroDecimal) {
          // 有非零小数部分，移除尾随零
          result = result.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
        } else {
          // 小数部分被截断为全0，使用默认的 decimalPrecision（4）或 integerPrecision 中较大的一个
          const defaultDecimalPrecision = 4;
          const zeros = "0".repeat(
            Math.max(
              integerPrecision,
              Math.min(decimalPrecision, defaultDecimalPrecision),
            ),
          );
          result = isNegative ? `-0.${zeros}` : `0.${zeros}`;
        }
      } else {
        // 值为0或0.0，返回格式化的零值
        const zeros = "0".repeat(integerPrecision);
        result = `0.${zeros}`;
      }
    } else {
      return null;
    }

    return result;
  } catch (error) {
    // log.error("formatSignificantDigits error:", error);
    return null;
  }
};
